import { useBooleanState } from '@cian/react-utils';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { handleAvailability } from './internal/utils/handleAvailability';
import { trackOpenPhonesClick } from './tracking';
import { ICommercialContactsMainControllerProps } from './types';
import { useApplicationContext } from '../../../containers/ApplicationContext';
import { useDynamicPhoneFlow } from '../../../hooks/useDynamicPhoneFlow';
import { useOfferDynamicCalltrackingEnabled } from '../../../hooks/useOfferDynamicCalltrackingEnabled';
import { useSimpleBotDetector } from '../../../hooks/useSimpleBotDetector';
import { selectDynamicPhones } from '../../../selectors/offerData/getActualPhones';
import { formatNumber } from '../../../utils/phone';
import { showPhonesLimitAuth } from '../../../utils/showPhonesLimitAuth';
import { trackShowStatNumber } from '../../../utils/tracking/trackShowStatNumber';
import { CommercialContactsMainView } from '../CommercialContactsMainView';

export const CommercialContactsMainController: React.FC<ICommercialContactsMainControllerProps> = props => {
  const {
    agent,
    user,
    offer,
    agentAvailability,
    setPhoneCollapse,
    offerChat,
    abGroup,
    phones,
    isCallTrackingButtonDisabled,
    openNotAvailablePopup,
  } = props;

  const ownOfferPopupState = useBooleanState();
  const phonesState = useBooleanState();
  const botDetector = useSimpleBotDetector();
  const { microfrontendsAPI } = useApplicationContext();

  const isOfferDynamicCalltrackingEnabled = useOfferDynamicCalltrackingEnabled();
  const { requestPhone } = useDynamicPhoneFlow();
  const [isDynamicPhoneLoading, setDynamicPhoneLoading] = React.useState(false);

  const dynamicPhones = useSelector(selectDynamicPhones);
  /** Подменные номера динКТ (SEL-998) приоритетнее статических из оффера */
  const phone = (dynamicPhones ?? phones).map(formatNumber).join(', ');

  const handleOpenPhones = async () => {
    // ДинКТ вторички (SEL-998)
    const clickId = isOfferDynamicCalltrackingEnabled ? uuid() : undefined;

    trackOpenPhonesClick({ offer, agent, user, abGroup, kpId: null, bot: !botDetector.isHuman(), clickId });

    if (clickId) {
      setDynamicPhoneLoading(true);
      const hasPhoneToShow = await requestPhone({
        offerId: offer.id,
        clickId,
        hasStaticPhones: phones.length > 0,
        onDegradation: () => trackShowStatNumber({ offer, agent, user, clickId }),
      });
      setDynamicPhoneLoading(false);

      if (!hasPhoneToShow) {
        return;
      }
    }

    setPhoneCollapse();

    phonesState.setTrue();
  };

  const handlePhoneClick = (): void => {
    /**
     * @todo Удалить блок с экспериментом
     * @description Данный функционал появился в задаче CD-129223, будет удалён в задаче CD-129224
     * Удалить, если эксперимент будет не удачным
     */
    /* istanbul ignore next */
    if (isCallTrackingButtonDisabled) {
      showPhonesLimitAuth(offer, agent, microfrontendsAPI);
    } else {
      handleOpenPhones();

      const isAgentNotAvailable = Boolean(agentAvailability && !agentAvailability.available);
      const showAvailability = offerChat.isChatsEnabled && isAgentNotAvailable;

      /* istanbul ignore next */
      if (showAvailability) {
        handleAvailability({
          user,
          agent,
          offer,
          onOwnOfferPopupOpen: ownOfferPopupState.setTrue,
          onOpenNotAvailablePopup: openNotAvailablePopup,
        });
      }
    }
  };

  return (
    <CommercialContactsMainView
      isPhoneOpen={phonesState.state}
      isPopupOwnOfferOpen={ownOfferPopupState.state}
      phone={phone}
      onPhoneClick={handlePhoneClick}
      isPhoneLoading={isDynamicPhoneLoading}
      onPopupOwnOfferClose={ownOfferPopupState.setFalse}
    />
  );
};

CommercialContactsMainController.displayName = 'CommercialContactsMainController';
